import type { DateRange } from "react-day-picker";

import { addMonths, format, subMonths } from "date-fns";
import { z } from "zod";

export const intervalUnitSchema = z
  .enum(["minute", "hour", "day"])
  .describe("IntervalUnit");

type IntervalUnit = z.infer<typeof intervalUnitSchema>;

const intervalUnitMultipliers: Record<IntervalUnit, number> = {
  minute: 60,
  hour: 3600,
  day: 86_400,
};

export function hours(hoursToAdd: number) {
  return hoursToAdd * 60 * 60 * 1000;
}

export function minutes(minutesToAdd: number) {
  return minutesToAdd * 60 * 1000;
}

export function convertDaysToMs(days: number) {
  return days * 24 * 60 * 60 * 1000;
}

export function convertMsToDays(ms: number) {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.floor(ms / msPerDay);
}

export const dateRangeFormatter = (dateRange: DateRange) => {
  if (!dateRange.from || !dateRange.to) {
    return "Select a date range";
  }
  return `${dateFormatter(dateRange.from)} - ${dateFormatter(dateRange.to)}`;
};

const dateFormatter = (date: Date) => format(date, "LLL dd, y");

export function minutesToSeconds(minutes: number) {
  return minutes * 60;
}

export function hoursToSeconds(hours: number) {
  return hours * 60 * 60;
}

export function timeUnitAsSeconds({
  unit,
  value,
}: {
  unit: IntervalUnit;
  value: number;
}): number {
  return value * intervalUnitMultipliers[unit];
}

export function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  } else if (ms < 60 * 1000) {
    return `${(ms / 1000).toFixed(2)}s`;
  } else if (ms < 60 * 60 * 1000) {
    const minutes = Math.floor(ms / (60 * 1000));
    const seconds = ((ms % (60 * 1000)) / 1000).toFixed(0);
    return `${minutes}m ${seconds}s`;
  } else {
    const hours = Math.floor(ms / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    return `${hours}h ${minutes}m`;
  }
}

export function formatDateToMonthDayHour(date: Date): string {
  return date
    .toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
    .replace(" at ", ", ");
}

/**
 * Finds the most recent monthiversary of the given date by adding months
 * until we find one in the future, then uses the previous one.
 */
export function getMostRecentMonthiversary(date: Date) {
  const now = new Date();
  let candidate = new Date(date);

  // Keep adding months until we find one in the future
  // Using date-fns to handle month-end edge cases (e.g., Jan 31 + 1 month = Feb 28, not Mar 3)
  while (candidate <= now) {
    candidate = addMonths(candidate, 1);
  }

  // Go back one month to get the most recent past monthiversary
  candidate = subMonths(candidate, 1);

  return candidate;
}

/**
 * Finds the next monthiversary of the given date by adding months
 * until we find one in the future, then uses the next one.
 */
export function getNextMonthiversary(date: Date) {
  const now = new Date();
  let candidate = new Date(date);

  // Keep adding months until we find one in the future
  // Using date-fns to handle month-end edge cases (e.g., Jan 31 + 1 month = Feb 28, not Mar 3)
  while (candidate <= now) {
    candidate = addMonths(candidate, 1);
  }

  return candidate;
}

export function unixTimestampToDateOrNull({
  value,
}: {
  value: number | undefined;
}): Date | null {
  if (value === undefined) {
    return null;
  }

  const asMilliseconds = value > 1_000_000_000_000 ? value : value * 1000;
  const parsedDate = new Date(asMilliseconds);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }
  return parsedDate;
}
